// SCREENS-VIEWER (stack contract §4.1–§4.3): one live connection to a lease's screen, as a plain
// state machine the `LiveScreen` component drives. It holds one RFB client (noVNC) at a time,
// opened on CompanyOS's relay with a short-lived viewer ticket for the role:
//   connecting → live → (socket lost) reconnecting → live …   · after N failed tries → error
//   any → ended (the lease ended, or its ticket is refused because it is no longer live)
// Watch is view-only twice over: the RFB client's `viewOnly` here, and the relay drops input.
// A spare ticket is fetched before the current one expires, so a reconnect does not wait on it;
// each ticket opens one connection only.

export type LiveRole = "watch" | "control";
export type LivePhase = "connecting" | "live" | "reconnecting" | "ended" | "error";

export type LiveState = {
  phase: LivePhase;
  /** Step text under the spinner ("Connecting…", "Reconnecting… try 2 of 5"). */
  step: string | null;
  /** Why it ended or failed, in plain words. */
  reason: string | null;
  role: LiveRole;
  /** Failed tries in a row (0 once live). */
  failures: number;
};

/** The part of noVNC's RFB this uses (and the tests fake). */
export interface RfbLike {
  viewOnly: boolean;
  scaleViewport: boolean;
  resizeSession: boolean;
  clipViewport: boolean;
  background: string;
  addEventListener(type: string, fn: (e: Event) => void): void;
  disconnect(): void;
  focus?(): void;
}

export type CreateRfb = (
  target: HTMLElement,
  url: string,
  options: { shared: boolean },
) => RfbLike | Promise<RfbLike>;

export type LiveTicket = { url: string; expires_at: string };

/**
 * A refusal that retrying cannot fix. `watch`: a control ticket is refused because someone else
 * drives now (a hand-back: the relay closed the control socket with 4401) — the connection goes
 * on as a watcher.
 */
export class LiveRefused extends Error {
  constructor(
    readonly outcome: "ended" | "error" | "watch",
    reason: string,
  ) {
    super(reason);
  }
}

type Timers = {
  set: (fn: () => void, ms: number) => unknown;
  clear: (h: unknown) => void;
};

export type LiveConnectionOptions = {
  target: HTMLElement;
  role: LiveRole;
  /** A new viewer ticket for the role; throws `LiveRefused` when it cannot be had at all. */
  fetchTicket: (role: LiveRole) => Promise<LiveTicket>;
  createRfb: CreateRfb;
  /** The ticket's `url` as a WebSocket URL. */
  socketUrl: (ticketUrl: string) => string;
  onState: (s: LiveState) => void;
  now?: () => number;
  timers?: Timers;
  maxFailures?: number;
  backoffMs?: readonly number[];
  connectTimeoutMs?: number;
  /** Fetch the spare ticket this long before the one in hand expires. */
  refreshLeadMs?: number;
  /**
   * A driver may ask the remote screen to take the pane's size (desktops). Off for browsers:
   * Camofox keeps its window size, so a resized X screen crops the page instead of fitting it.
   */
  resizeRemote?: boolean;
};

export type Size = { width: number; height: number };
export type FitRect = { scale: number; width: number; height: number; left: number; top: number };

/**
 * Where the remote screen sits in the pane: scaled to contain (aspect kept, never cropped, never
 * larger than the pane), top-aligned, centred across. noVNC's `scaleViewport` computes the same
 * scale (Display.autoscale); `alignTop` puts its canvas at the top.
 */
export function fitRect(pane: Size, remote: Size): FitRect {
  if (pane.width <= 0 || pane.height <= 0 || remote.width <= 0 || remote.height <= 0)
    return { scale: 0, width: 0, height: 0, left: 0, top: 0 };
  const scale =
    remote.width / remote.height >= pane.width / pane.height
      ? pane.width / remote.width
      : pane.height / remote.height;
  const width = Math.min(pane.width, remote.width * scale);
  const height = Math.min(pane.height, remote.height * scale);
  return { scale, width, height, left: (pane.width - width) / 2, top: 0 };
}

/** noVNC centres its canvas both ways (`margin: auto`); keep it centred across, at the top. */
export function alignTop(target: HTMLElement): void {
  if (typeof target.querySelectorAll !== "function") return;
  for (const c of target.querySelectorAll("canvas")) c.style.margin = "0 auto";
}

/**
 * The canvas is drawn larger than the pane (a scale noVNC has not caught up with, e.g. a remote
 * resize the server clamped): 1 px of rounding allowed.
 */
export function overflows(target: HTMLElement): boolean {
  if (typeof target.querySelector !== "function") return false;
  const c = target.querySelector("canvas");
  if (!c) return false;
  const p = target.getBoundingClientRect();
  const r = c.getBoundingClientRect();
  return r.width > p.width + 1 || r.height > p.height + 1;
}

export const MAX_FAILURES = 5;
export const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000] as const;
/** A connection that lived this long dropped by accident; a shorter one counts as a failure. */
const STABLE_MS = 5_000;

export class LiveConnection {
  private readonly o: Required<Omit<LiveConnectionOptions, "target" | "role" | "resizeRemote">> & {
    resizeRemote: boolean;
    target: HTMLElement;
  };
  private role: LiveRole;
  private state: LiveState;
  private rfb: RfbLike | null = null;
  /** Bumped on every attempt and teardown: events of an older RFB are ignored. */
  private gen = 0;
  private spare: LiveTicket | null = null;
  private refreshTimer: unknown = null;
  private retryTimer: unknown = null;
  private connectTimer: unknown = null;
  private stopped = false;
  /** Bumped whenever the spare-ticket refresh is dropped: late answers are ignored. */
  private refreshGen = 0;

  constructor(opts: LiveConnectionOptions) {
    this.o = {
      now: () => Date.now(),
      timers: { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as number) },
      maxFailures: MAX_FAILURES,
      backoffMs: BACKOFF_MS,
      connectTimeoutMs: 15_000,
      refreshLeadMs: 10_000,
      ...opts,
      resizeRemote: opts.resizeRemote ?? true,
    };
    this.role = opts.role;
    this.state = {
      phase: "connecting",
      step: "Connecting…",
      reason: null,
      role: opts.role,
      failures: 0,
    };
  }

  get current(): LiveState {
    return this.state;
  }

  start(): void {
    this.stopped = false;
    this.set({ phase: "connecting", step: "Connecting…", reason: null, failures: 0 });
    void this.attempt();
  }

  /** Watch ↔ control: a fresh ticket for the new role, a new connection. */
  setRole(role: LiveRole): void {
    if (role === this.role && this.state.phase !== "error") return;
    this.role = role;
    this.spare = null;
    this.cancelRefresh();
    this.teardown();
    this.state = { ...this.state, role };
    if (this.stopped) return;
    this.set({ phase: "connecting", step: "Connecting…", reason: null, failures: 0 });
    void this.attempt();
  }

  /** Try again after an error (the pane's "Try again"). */
  retry(): void {
    if (this.stopped || this.state.phase === "ended") return;
    this.teardown();
    this.set({ phase: "connecting", step: "Connecting…", reason: null, failures: 0 });
    void this.attempt();
  }

  /**
   * The pane changed size: scale the remote screen to fit it again (aspect ratio kept, top
   * aligned). noVNC re-computes the scale when the flag is set; a canvas still drawn larger than
   * the pane is scaled again from scratch.
   */
  refit(): void {
    const r = this.rfb;
    if (!r) return;
    alignTop(this.o.target);
    if (overflows(this.o.target)) r.scaleViewport = false;
    r.scaleViewport = true;
    // Setting it again asks noVNC to send the pane's size to the server (driving a desktop).
    if (this.resizes()) r.resizeSession = true;
  }

  private resizes(): boolean {
    return this.role === "control" && this.o.resizeRemote;
  }

  /** The lease ended: close and say so. */
  end(reason = "This session has ended."): void {
    this.stop();
    this.set({ phase: "ended", step: null, reason });
  }

  stop(): void {
    this.stopped = true;
    this.cancelRefresh();
    this.clearTimer("retryTimer");
    this.teardown();
  }

  // ----------------------------------------------------------------------------------------

  private set(p: Partial<LiveState>) {
    this.state = { ...this.state, ...p, role: this.role };
    this.o.onState(this.state);
  }

  private clearTimer(k: "refreshTimer" | "retryTimer" | "connectTimer") {
    if (this[k] !== null) this.o.timers.clear(this[k]);
    this[k] = null;
  }

  private teardown() {
    this.gen += 1;
    this.clearTimer("connectTimer");
    this.clearTimer("retryTimer");
    const r = this.rfb;
    this.rfb = null;
    if (r) {
      try {
        r.disconnect();
      } catch {} // already closed
    }
  }

  private fresh(t: LiveTicket | null): t is LiveTicket {
    return !!t && Date.parse(t.expires_at) - this.o.now() > 5_000;
  }

  /** A ticket for this connection: the spare if it is still good, else a new one. */
  private async ticket(): Promise<LiveTicket> {
    const spare = this.spare;
    this.spare = null;
    if (this.fresh(spare)) return spare;
    return this.o.fetchTicket(this.role);
  }

  /** Drops the spare-ticket refresh: its timer, and any answer still on the way. */
  private cancelRefresh() {
    this.refreshGen += 1;
    this.clearTimer("refreshTimer");
  }

  /** Keeps one unused, unexpired ticket in hand for the next reconnect. */
  private scheduleRefresh(after: LiveTicket | null) {
    this.cancelRefresh();
    if (this.stopped) return;
    const rg = this.refreshGen;
    const current = () => rg === this.refreshGen && !this.stopped;
    const wait = after
      ? Math.max(0, Date.parse(after.expires_at) - this.o.now() - this.o.refreshLeadMs)
      : 0;
    const role = this.role;
    this.refreshTimer = this.o.timers.set(() => {
      this.refreshTimer = null;
      if (!current()) return;
      this.o.fetchTicket(role).then(
        (t) => {
          if (!current()) return;
          this.spare = t;
          this.scheduleRefresh(t);
        },
        (e) => {
          if (!current()) return;
          if (e instanceof LiveRefused) {
            if (e.outcome === "ended") this.end(e.message);
            return; // the next reconnect reports it
          }
          // A blip: try again shortly.
          this.refreshTimer = this.o.timers.set(() => {
            this.refreshTimer = null;
            if (current()) this.scheduleRefresh(null);
          }, 5_000);
        },
      );
    }, wait);
  }

  private async attempt(): Promise<void> {
    this.teardown();
    const gen = this.gen;
    const att = { connected: false };
    // One deadline for the whole try: the ticket, loading the client and the RFB handshake.
    this.connectTimer = this.o.timers.set(() => {
      this.connectTimer = null;
      if (gen !== this.gen || att.connected) return;
      this.teardown();
      this.fail("The screen took too long to answer.");
    }, this.o.connectTimeoutMs);
    let t: LiveTicket;
    try {
      t = await this.ticket();
    } catch (e) {
      if (gen !== this.gen || this.stopped) return;
      if (e instanceof LiveRefused) {
        if (e.outcome === "ended") this.end(e.message);
        // Handed back (or taken over by someone else): keep watching instead of failing.
        else if (e.outcome === "watch" && this.role === "control") this.setRole("watch");
        else this.fail(e.message, true);
        return;
      }
      this.fail("The screen didn't answer.");
      return;
    }
    if (gen !== this.gen || this.stopped) return;
    // This ticket is spent on this connection: get the next one ready.
    this.scheduleRefresh(t);
    let rfb: RfbLike;
    try {
      rfb = await this.o.createRfb(this.o.target, this.o.socketUrl(t.url), { shared: true });
    } catch {
      if (gen !== this.gen || this.stopped) return;
      this.fail("The viewer couldn't start.");
      return;
    }
    if (gen !== this.gen || this.stopped) {
      try {
        rfb.disconnect();
      } catch {}
      return;
    }
    this.rfb = rfb;
    rfb.viewOnly = this.role === "watch";
    // Always fit the pane, aspect kept, top aligned (no physical size, no scrolling, no crop). A
    // person who drives a desktop asks the remote screen to take the pane's size (crisp, 1:1
    // pixels) where the server supports it (ExtendedDesktopSize); a refused or clamped resize stays
    // scaled to fit. Browsers and watchers never resize the remote screen.
    rfb.scaleViewport = true;
    rfb.resizeSession = this.resizes();
    rfb.clipViewport = false;
    rfb.background = "transparent";
    alignTop(this.o.target);
    let liveSince = 0;
    rfb.addEventListener("connect", () => {
      if (gen !== this.gen) return;
      att.connected = true;
      liveSince = this.o.now();
      this.clearTimer("connectTimer");
      // Enforced again once connected (noVNC applies it to its input handlers).
      rfb.viewOnly = this.role === "watch";
      // Failures are not forgiven yet: only a connection that stays up resets them.
      this.set({ phase: "live", step: null, reason: null });
    });
    rfb.addEventListener("disconnect", () => {
      if (gen !== this.gen) return;
      this.rfb = null;
      this.gen += 1;
      this.clearTimer("connectTimer");
      // A control socket closed (a hand-back closes it with 4401, which noVNC does not report):
      // never reuse a control ticket minted before; the fresh one is refused if control was
      // handed back, and the connection goes on as a watcher.
      if (this.role === "control") {
        this.spare = null;
        this.cancelRefresh();
      }
      const connected = att.connected;
      if (connected && this.o.now() - liveSince >= STABLE_MS) {
        // It was live for a while: reconnect right away (not a failure yet).
        this.set({ phase: "reconnecting", step: "Reconnecting…", reason: null, failures: 0 });
        void this.attempt();
      }
      // Never connected, or dropped straight after: a failure (so a flapping socket backs off).
      else
        this.fail(
          connected ? "The screen dropped the connection." : "Couldn't connect to the screen.",
        );
    });
    rfb.addEventListener("securityfailure", (e) => {
      if (gen !== this.gen) return;
      const reason = (e as CustomEvent<{ reason?: string }>).detail?.reason;
      this.teardown();
      this.fail(
        reason ? `The screen refused the viewer: ${reason}` : "The screen refused the viewer.",
        true,
      );
    });
    rfb.addEventListener("credentialsrequired", () => {
      if (gen !== this.gen) return;
      this.teardown();
      this.fail("The screen asked for a password the relay should have handled.", true);
    });
  }

  private fail(reason: string, final = false) {
    if (this.stopped) return;
    this.clearTimer("connectTimer");
    const failures = this.state.failures + 1;
    if (final || failures >= this.o.maxFailures) {
      this.cancelRefresh();
      this.spare = null;
      this.set({
        phase: "error",
        step: null,
        failures,
        reason: final ? reason : `${reason} Gave up after ${failures} tries.`,
      });
      return;
    }
    const wait = this.o.backoffMs[Math.min(failures - 1, this.o.backoffMs.length - 1)] ?? 1_000;
    this.set({
      phase: "reconnecting",
      step: `Reconnecting… try ${failures + 1} of ${this.o.maxFailures}`,
      reason,
      failures,
    });
    this.retryTimer = this.o.timers.set(() => {
      this.retryTimer = null;
      void this.attempt();
    }, wait);
  }
}
